import { useGSAP } from "@gsap/react"
import clsx from "clsx"
import gsap from "gsap"
import { FC, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { AvatarDefaultIcon } from "@/assets"
import { useMatchConfirm } from "@/hooks/useMatchConfirm"
import { useMatchSearch } from "@/hooks/useMatchSearch"
import { useModals } from "@/modals"
import { useAppDispatch, useAppSelector } from "@/store"
import { COMMON_BID_VARIANT_ID } from "@/store/apis/matchAPI"
import { useGetPlayerProfileQuery } from "@/store/apis/playerAPI"
import { FindMatchSelectors, setFindMatchMode } from "@/store/slices/findMatch"
import { MatchSelectors } from "@/store/slices/match"
import { Button } from "@/ui/Button"
import styles from "./FindMatchLabel.module.scss"

export const FindMatchLabel: FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { openModal } = useModals()
  const { isMatchFound } = useMatchSearch()
  const { player, rival, matchConfirmStatus } = useMatchConfirm()

  const matchSession = useAppSelector(MatchSelectors.selectMatchSession())
  const findMatchMode = useAppSelector(FindMatchSelectors.selectFindMatchMode())

  const [isFindMatch, setIsFindMatch] = useState<boolean>(false)
  const [matchType, setMatchType] = useState<"common" | "ton">("common")

  const refRoot = useRef<HTMLDivElement>(null)
  const refTl = useRef<gsap.core.Timeline>(null)

  const { data: playerProfile } = useGetPlayerProfileQuery()

  const title = useMemo(() => {
    if (matchType === "common") {
      return t("findMatchLabel.common.title")
    }

    return t("findMatchLabel.ton.title")
  }, [matchType])

  const text = useMemo(() => {
    if (matchType === "common") {
      if (isMatchFound) {
        return t("findMatchLabel.common.text.creating")
      } else {
        return t("findMatchLabel.common.text.search")
      }
    }

    switch (matchConfirmStatus) {
      case "WAITING_FOR_OPPONENT":
      case "FINISHED": {
        return t("findMatchLabel.ton.text.search")
      }
      case "WAITING_FOR_YOUR_CONFIRM": {
        return t("findMatchLabel.ton.text.confirm.you")
      }
      case "WAITING_FOR_OPPONENT_CONFIRM": {
        return t("findMatchLabel.ton.text.confirm.rival")
      }
      default: {
        return t("findMatchLabel.ton.text.creating")
      }
    }
  }, [matchType, matchSession, isMatchFound, matchConfirmStatus, player, rival])

  const handleOpen = () => {
    if (playerProfile?.inQueue) {
      dispatch(setFindMatchMode("modal"))
      if (playerProfile.matchBidVariant === COMMON_BID_VARIANT_ID) {
        openModal("findMatch")
      } else {
        openModal("findTonMatch")
      }
      return
    }

    if (matchSession?.status === "PENDING") {
      dispatch(setFindMatchMode("modal"))
      openModal("findTonMatch")
      return
    }
  }

  useEffect(() => {
    if (playerProfile?.inQueue) {
      setIsFindMatch(true)

      if (playerProfile.matchBidVariant === COMMON_BID_VARIANT_ID) {
        setMatchType("common")
      } else {
        setMatchType("ton")
      }

      return
    }

    if (matchSession?.status === "PENDING") {
      setIsFindMatch(true)
      setMatchType("ton")
      return
    }

    setIsFindMatch(false)
  }, [playerProfile?.inQueue, matchSession?.status])

  const isActive = useMemo(() => {
    return isFindMatch && findMatchMode === "label"
  }, [isFindMatch, findMatchMode])

  useGSAP(() => {
    refTl.current = gsap
      .timeline({
        paused: true
      })
      .fromTo(
        refRoot.current,
        {
          autoAlpha: 0,
          yPercent: -100,
          scale: 0.8
        },
        {
          autoAlpha: 1,
          yPercent: 0,
          scale: 1,
          ease: "back.out",
          duration: 0.5
        }
      )
  }, [])

  useGSAP(() => {
    if (isActive) {
      refTl.current?.timeScale(1)
      refTl.current?.play()
    } else {
      refTl.current?.timeScale(2)
      refTl.current?.reverse()
    }
  }, [isActive])

  return (
    <div
      className={clsx(styles.FindMatchLabel, styles[matchType])}
      ref={refRoot}
    >
      <div className={clsx(styles.FindMatchLabel_left)}>
        <div className={clsx(styles.FindMatchLabel_img)}>
          <AvatarDefaultIcon />
        </div>
        <div className={clsx(styles.FindMatchLabel_info)}>
          <div className={clsx(styles.FindMatchLabel_title)}>{title}</div>
          <div className={clsx(styles.FindMatchLabel_text)}>{text}</div>
        </div>
      </div>
      <div className={clsx(styles.FindMatchLabel_buttons)}>
        <Button
          className={clsx(styles.FindMatchLabel_buttons_item)}
          styles={"center"}
          onClick={handleOpen}
        >
          {t("button.open")}
        </Button>
      </div>
    </div>
  )
}
